import type { GarageKind } from '@accra/shared';
import { playHorn } from '../audio/horns';
import { equippedPaint, equippedStickerText, type GarageState } from '../meta/garage';
import type { Profile } from '../meta/profile';
import { buyItem } from '../net/garage';
import type { Garage } from '../ui/screens/Garage';

export interface GarageHost {
  profile(): Profile;
  setProfile(profile: Profile): void;
  playerKey(): string | null;
  /** Repaint the trotro in the scene. */
  refreshTrotro(): void;
}

export function garageState(profile: Profile): GarageState {
  return { owned: profile.owned, unlocks: profile.unlocks, equipped: profile.equipped };
}

export function trotroLook(profile: Profile) {
  const state = garageState(profile);
  return { paint: equippedPaint(state), sticker: equippedStickerText(state) };
}

/** Equip and buy garage items; purchases go through the server so the wallet stays honest. */
export class GarageController {
  constructor(private readonly host: GarageHost, private readonly screen: () => Garage) {}

  equip(kind: GarageKind, id: string, text?: string): void {
    const profile = this.host.profile();
    const equipped = { ...profile.equipped };
    if (kind === 'paint') equipped.paint = id;
    if (kind === 'horn') {
      equipped.horn = id;
      playHorn(id);
    }
    if (kind === 'sticker') {
      equipped.sticker = id;
      equipped.customSticker = id === 'custom' ? (text ?? null) : null;
    }
    this.host.setProfile({ ...profile, equipped });
    this.host.refreshTrotro();
    this.redraw();
  }

  async buy(kind: GarageKind, id: string, text?: string): Promise<void> {
    this.screen().say('Buying…');
    const result = await buyItem(this.host.playerKey(), kind, id, text);
    if (!result.ok) return this.screen().say(result.message);
    const profile = this.host.profile();
    this.host.setProfile({ ...profile, owned: result.owned, walletPesewas: result.wallet.balancePesewas });
    if (kind === 'upgrade') {
      this.screen().say('Upgraded! It lasts longer from your next shift.');
      return this.redraw();
    }
    this.screen().say('Bought! Now in use.');
    this.equip(kind, id, text);
  }

  preview(id: string): void {
    playHorn(id);
  }

  redraw(): void {
    const profile = this.host.profile();
    this.screen().update(garageState(profile), profile.walletPesewas);
  }
}
